import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { STATE_FORMATS, STATE_OPTIONS } from '../config/pesticideReportConfigs';
import api from '../services/api';

const API = process.env.REACT_APP_API_URL || 'https://api.safecorepest.com';
const GREEN = '#2D5C3F';
const GOLD  = '#D4AF37';
const LIGHT = '#f0f4f1';

const fmtMoney = (n) => n != null ? '$' + parseFloat(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '$0.00';

const KpiCard = ({ icon, label, value, sub, trend, color }) => (
  <div style={{ background: 'white', borderRadius: '10px', padding: '20px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)', borderLeft: `4px solid ${color || GREEN}` }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <div>
        <p style={{ margin: '0 0 4px', fontSize: '12px', fontWeight: '600', color: '#9CA3AF', textTransform: 'uppercase' }}>{label}</p>
        <p style={{ margin: 0, fontSize: '26px', fontWeight: '700', color: '#111827' }}>{value}</p>
        {sub && <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#6B7280' }}>{sub}</p>}
      </div>
      <span style={{ fontSize: '28px' }}>{icon}</span>
    </div>
    {trend != null && (
      <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '4px' }}>
        <span style={{ fontSize: '13px', fontWeight: '600', color: trend >= 0 ? '#059669' : '#DC2626' }}>
          {trend >= 0 ? '▲' : '▼'} {Math.abs(trend)}%
        </span>
        <span style={{ fontSize: '12px', color: '#9CA3AF' }}>vs last month</span>
      </div>
    )}
  </div>
);

const BarChart = ({ data, valueKey, labelKey, color, height = 120, formatValue }) => {
  if (!data || data.length === 0) return <div style={{ textAlign: 'center', color: '#9CA3AF', padding: '20px', fontSize: '13px' }}>No data available</div>;
  const max = Math.max(...data.map(d => d[valueKey] || 0), 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', height: height + 40, paddingTop: '8px' }}>
      {data.map((d, i) => (
        <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
          <span style={{ fontSize: '11px', color: '#374151', fontWeight: '600' }}>
            {formatValue ? formatValue(d[valueKey]) : d[valueKey]}
          </span>
          <div style={{ width: '100%', background: '#F3F4F6', borderRadius: '4px 4px 0 0',
            height: height + 'px', display: 'flex', alignItems: 'flex-end' }}>
            <div style={{ width: '100%', borderRadius: '4px 4px 0 0',
              height: Math.max((d[valueKey] / max) * height, d[valueKey] > 0 ? 4 : 0) + 'px',
              background: color || GREEN, transition: 'height 0.3s ease' }} />
          </div>
          <span style={{ fontSize: '10px', color: '#9CA3AF', textAlign: 'center', lineHeight: '1.2' }}>{d[labelKey]}</span>
        </div>
      ))}
    </div>
  );
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month:'short', day:'2-digit', year:'numeric' }) : '—';
const fmtTime = (t) => t ? t.slice(0,5) : '—';
const fmtDT   = (d) => d ? new Date(d).toLocaleString('en-US', { month:'short', day:'2-digit', hour:'2-digit', minute:'2-digit' }) : '—';
const fmtMin  = (m) => m != null ? Math.round(m) + ' min' : '—';

const METHOD_LABELS = {
  spot_treatment:'Spot Treatment', gel_placement:'Gel Placement',
  bait_station:'Bait Station', crack_crevice:'Crack & Crevice',
  broadcast:'Broadcast', trap_placement:'Trap Placement',
  void_treatment:'Void Treatment', perimeter:'Perimeter',
};


// ── Export CSV ─────────────────────────────────────────────────────────────
function openInvoicePdf(invoiceId, invoiceNumber, setInvoicePdf, setInvoicePdfLoading) {
  setInvoicePdf({ id: invoiceId, url: null, invoice_number: invoiceNumber });
  setInvoicePdfLoading(true);
  const token = localStorage.getItem('token');
  fetch(`https://api.safecorepest.com/api/invoices/${invoiceId}/pdf`, {
    headers: { Authorization: `Bearer ${token}` }
  })
  .then(res => { if (!res.ok) throw new Error('Failed'); return res.blob(); })
  .then(blob => {
    const url = URL.createObjectURL(blob);
    setInvoicePdf({ id: invoiceId, url, invoice_number: invoiceNumber });
  })
  .catch(err => { alert('Error generating PDF: ' + err.message); setInvoicePdf(null); })
  .finally(() => setInvoicePdfLoading(false));
}

function exportCSV(reportId, data) {
  if (!data.length) return;
  const cols = Object.keys(data[0]);
  const header = cols.join(',');
  const rows = data.map(r =>
    cols.map(c => {
      const v = r[c];
      if (v === null || v === undefined) return '';
      const str = Array.isArray(v) ? v.join(';') : String(v);
      return str.includes(',') || str.includes('"') || str.includes('\n')
        ? '"' + str.replace(/"/g, '""') + '"'
        : str;
    }).join(',')
  );
  const csv = [header, ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url;
  a.download = reportId + '_' + new Date().toISOString().slice(0,10) + '.csv';
  a.click();
  URL.revokeObjectURL(url);
}

// ── Print ──────────────────────────────────────────────────────────────────
const PRINT_COLS = {
  'chemical-log': [
    { key:'applied_at',         label:'Date/Time',       wrap:false },
    { key:'facility_name',      label:'Facility',        wrap:true  },
    { key:'client_name',        label:'Client',          wrap:true  },
    { key:'product_name',       label:'Product',         wrap:true  },
    { key:'epa_number',         label:'EPA #',           wrap:false },
    { key:'active_ingredient',  label:'Active Ingr.',    wrap:true  },
    { key:'application_method', label:'Method',          wrap:false },
    { key:'quantity',           label:'Qty',             wrap:false },
    { key:'unit',               label:'Unit',            wrap:false },
    { key:'dilution_rate',      label:'Dilution',        wrap:false },
    { key:'area_name',          label:'Area',            wrap:false },
    { key:'tech_first',         label:'Tech',            wrap:false },
    { key:'order_number',       label:'WO #',            wrap:false },
  ],
  'service-history': [
    { key:'order_number',       label:'WO #',            wrap:false },
    { key:'scheduled_date',     label:'Date',            wrap:false },
    { key:'facility_name',      label:'Facility',        wrap:true  },
    { key:'client_name',        label:'Client',          wrap:true  },
    { key:'service_type',       label:'Type',            wrap:false },
    { key:'status',             label:'Status',          wrap:false },
    { key:'scheduled_time_start',label:'Sched Start',   wrap:false },
    { key:'scheduled_time_end', label:'Sched End',       wrap:false },
    { key:'actual_minutes',     label:'Actual Min',      wrap:false },
    { key:'tech_first',         label:'Tech First',      wrap:false },
    { key:'tech_last',          label:'Tech Last',       wrap:false },
    { key:'service_price',      label:'Price',           wrap:false },
    { key:'invoice_status',     label:'Inv Status',      wrap:false },
  ],
  'pesticide-annual': [
    { key:'applied_at',          label:'Date/Time',       wrap:false },
    { key:'facility_name',       label:'Site/Facility',   wrap:true  },
    { key:'client_name',         label:'Client',          wrap:true  },
    { key:'product_name',        label:'Product',         wrap:true  },
    { key:'epa_number',          label:'EPA Reg #',       wrap:false },
    { key:'active_ingredient',   label:'Active Ingr.',    wrap:true  },
    { key:'application_method',  label:'Method',          wrap:false },
    { key:'quantity',            label:'Qty',             wrap:false },
    { key:'unit',                label:'Unit',            wrap:false },
    { key:'dilution_rate',       label:'Dilution',        wrap:false },
    { key:'area_name',           label:'Area',            wrap:false },
    { key:'order_number',        label:'WO #',            wrap:false },
  ],
  'invoice-register': [
    { key:'invoice_number',     label:'Invoice #',       wrap:false },
    { key:'invoice_date',       label:'Date',            wrap:false },
    { key:'due_date',           label:'Due',             wrap:false },
    { key:'client_name',        label:'Client',          wrap:true  },
    { key:'facility_name',      label:'Facility',        wrap:true  },
    { key:'order_number',       label:'WO #',            wrap:false },
    { key:'status',             label:'Status',          wrap:false },
    { key:'total_amount',       label:'Total',           wrap:false },
    { key:'paid_amount',        label:'Paid',            wrap:false },
    { key:'payment_method',     label:'Method',          wrap:false },
    { key:'paid_at',            label:'Paid On',         wrap:false },
  ],
};

function printReport(reportId, data, totals, reportMeta) {
  if (!data.length) return;

  const cols = PRINT_COLS[reportId] || Object.keys(data[0]).map(k => ({ key:k, label:k.replace(/_/g,' '), wrap:false }));

  const fmtVal = (v) => {
    if (v === null || v === undefined) return '—';
    if (Array.isArray(v)) return v.join(', ');
    if (typeof v === 'string' && v.match(/^\d{4}-\d{2}-\d{2}T/)) {
      return new Date(v).toLocaleString('en-US',{month:'short',day:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit'});
    }
    if (typeof v === 'string' && v.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return new Date(v+'T12:00:00').toLocaleDateString('en-US',{month:'short',day:'2-digit',year:'numeric'});
    }
    return String(v);
  };

  const headers = cols.map(c => '<th>' + c.label + '</th>').join('');
  const rows = data.map((r,i) =>
    '<tr class="' + (i%2===0?'even':'odd') + '">' +
    cols.map(c => {
      const v = r[c.key];
      return '<td style="' + (c.wrap ? 'white-space:normal;max-width:140px;' : 'white-space:nowrap;') + '">'
        + fmtVal(v) + '</td>';
    }).join('') + '</tr>'
  ).join('');

  const totalsHtml = totals ? `
    <div class="totals">
      <span><strong>Billed:</strong> $${parseFloat(totals.total_billed).toFixed(2)}</span>
      <span><strong>Paid:</strong> $${parseFloat(totals.total_paid).toFixed(2)}</span>
      <span><strong>Pending:</strong> $${parseFloat(totals.total_pending).toFixed(2)}</span>
      <span><strong>Overdue:</strong> $${parseFloat(totals.total_overdue).toFixed(2)}</span>
    </div>` : '';

  const win = window.open('', '_blank');
  win.document.write(`<!DOCTYPE html><html><head>
    <title>${reportMeta?.label || reportId}</title>
    <style>
      @page { size: landscape; margin: 12mm 10mm; }
      * { box-sizing: border-box; }
      body { font-family: Arial, sans-serif; font-size: 9px; color: #222; margin: 0; }
      .header { display:flex; justify-content:space-between; align-items:flex-end; margin-bottom:8px; border-bottom:2px solid #2D5C3F; padding-bottom:6px; }
      .header h2 { color:#2D5C3F; margin:0; font-size:14px; }
      .header .meta { color:#666; font-size:9px; text-align:right; }
      .totals { display:flex; gap:20px; margin-bottom:10px; background:#f0f4f1; padding:6px 10px; border-radius:4px; }
      .totals span { font-size:10px; }
      table { width:100%; border-collapse:collapse; table-layout:auto; }
      th { background:#2D5C3F; color:#fff; padding:5px 6px; text-align:left; font-size:8px; white-space:nowrap; }
      td { padding:4px 6px; border-bottom:1px solid #eee; font-size:8px; vertical-align:top; word-break:break-word; }
      tr.even td { background:#fff; }
      tr.odd  td { background:#f9fafb; }
      .footer { margin-top:10px; font-size:8px; color:#aaa; display:flex; justify-content:space-between; }
      @media print {
        body { font-size:8px; }
        .header h2 { font-size:12px; }
        th, td { font-size:7.5px; padding:3px 5px; }
      }
    </style>
  </head><body>
    <div class="header">
      <h2>${reportMeta?.icon || ''} ${reportMeta?.label || reportId}</h2>
      <div class="meta">
        Generated: ${new Date().toLocaleString('en-US')}<br/>
        ${data.length} records
      </div>
    </div>
    ${totalsHtml}
    <table><thead><tr>${headers}</tr></thead><tbody>${rows}</tbody></table>
    <div class="footer">
      <span>SafeCore Pest Management</span>
      <span>Confidential — Internal Use Only</span>
    </div>
    <script>window.onload = function(){ window.print(); }<\/script>
  </body></html>`);
  win.document.close();
}



const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({length: 5}, (_,i) => CURRENT_YEAR - i);

const REPORT_CATALOG = [
  { section: 'Regulatory (Chemical)', icon: '⚗️', reports: [
    { id: 'chemical-log',     label: 'Chemical Application Log',  icon: '📋', available: true,  desc: 'All pesticide applications with EPA#, AI, technician and location.' },
    { id: 'pesticide-annual', label: 'Pesticide Annual Report',   icon: '📄', available: true,  desc: 'Annual pesticide application report — select state format (NY, NJ, FL, TX, CA).' },
  ]},
  { section: 'Pest Control', icon: '🐛', reports: [
    { id: 'service-history', label: 'Service History',         icon: '🗓️', available: true,  desc: 'Complete work order history with status, timing and technician.' },
    { id: 'device-log',      label: 'Device Inspection Log',   icon: '📍', available: false, desc: 'Device-by-device inspection results and catch activity.' },
    { id: 'device-trends',   label: 'Device Activity Trends',  icon: '📈', available: false, desc: 'Trend analysis of device catches over time.' },
    { id: 'conducive',       label: 'Conducive Conditions',    icon: '⚠️', available: false, desc: 'Open and resolved conducive conditions by facility.' },
  ]},
  { section: 'Technician / Operations', icon: '👷', reports: [
    { id: 'timing',          label: 'Timing & Performance',        icon: '⏱️', available: true,  desc: 'Scheduled vs actual service times and technician KPIs.', link: '/timing' },
    { id: 'tech-production', label: 'Technician Production',       icon: '📊', available: true,  desc: 'Services completed, revenue and stop counts per technician.' },
    { id: 'completion',      label: 'Service Completion Progress',  icon: '✅', available: false, desc: 'Completion rates by route, facility and period.' },
    { id: 'license-status',  label: 'License Status',              icon: '🪪', available: false, desc: 'Technician license expiry tracking and compliance status.' },
  ]},
  { section: 'Financial', icon: '💰', reports: [
    { id: 'invoice-register', label: 'Invoice Register',     icon: '🧾', available: true,  desc: 'All invoices with amounts, status and payment details.' },
    { id: 'ar',               label: 'Accounts Receivable',  icon: '💳', available: true,  desc: 'Outstanding balances by client and aging buckets.' },
    { id: 'collections',      label: 'Collections',          icon: '📬', available: false, desc: 'Overdue invoices and collection activity log.' },
  ]},
];

export default function Reports() {
  useAuth(); // keep auth check
  const token = localStorage.getItem('token');
  const navigate  = useNavigate();
  const location  = useLocation();

  const qp = new URLSearchParams(location.search);
  const [activeReport, setActiveReport] = useState(qp.get('view') || null);

  const [filters, setFilters]   = useState({ facilities:[], technicians:[], clients:[] });
  const [loading, setLoading]   = useState(false);
  const [invoicePdf, setInvoicePdf] = useState(null);
  const [invoicePdfLoading, setInvoicePdfLoading] = useState(false);
  const [data,    setData]      = useState([]);
  const [totals,  setTotals]    = useState(null);
  const [filter,  setFilter]    = useState({
    facility_id:'', start_date:'', end_date:'', status:'', technician_id:'', client_id:'', user_id:'', year: String(new Date().getFullYear()), state: 'NY'
  });

  const headers = { Authorization: 'Bearer ' + token };

  // ── Analytics state ──────────────────────────────────────────────────────
  const [analytics, setAnalytics]               = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);

  useEffect(() => {
    api.get('/analytics/dashboard')
      .then(res => { if (res.data.success) setAnalytics(res.data.data); })
      .catch(e => console.warn('Analytics fetch failed', e))
      .finally(() => setAnalyticsLoading(false));
  }, []);

  useEffect(() => {
    fetch(API + '/api/reports/filters', { headers })
      .then(r => r.json()).then(d => { if (d.success) setFilters(d); });
  }, []);

  const runReport = async (reportId, currentFilter) => {
    if (!reportId) return;
    const f = currentFilter || filter;
    setLoading(true); setData([]); setTotals(null);
    const params = new URLSearchParams();
    Object.entries(f).forEach(([k,v]) => {
      if (!v) return;
      // AR uses as_of_date instead of end_date
      if (k === 'end_date' && reportId === 'ar') { params.append('as_of_date', v); return; }
      params.append(k, v);
    });
    try {
      const r = await fetch(API + '/api/reports/' + reportId + '?' + params, {
        headers: { Authorization: 'Bearer ' + token }
      });
      const d = await r.json();
      if (d.success) { setData(d.data); setTotals(d.totals || null); }
    } catch(e) { console.error('runReport error:', e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (activeReport && activeReport !== 'timing') {
      runReport(activeReport, filter);
    }
  }, [activeReport]);

  const openReport = (r) => {
    if (!r.available) return;
    if (r.link) { navigate(r.link); return; }
    setActiveReport(r.id);
    setData([]); setTotals(null);
    navigate('/reports?view=' + r.id, { replace: true });
  };

  const s = styles;

  /* ── HUB ── */
  if (!activeReport) return (
    <Layout>
      <div style={s.page}>
        <div style={s.pageHeader}>
          <h1 style={s.pageTitle}>📊 Business Analytics</h1>
          <p style={s.pageSubtitle}>Performance overview and operational reports</p>
        </div>

        {/* ── KPI CARDS ── */}
        {analyticsLoading ? (
          <div style={{ textAlign: 'center', padding: '32px', color: '#9CA3AF' }}>Loading analytics...</div>
        ) : analytics && (
          <>
            {/* Row 1 — 4 KPIs */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '20px' }}>
              <KpiCard icon="💰" label="Revenue This Month" color={GREEN}
                value={fmtMoney(analytics.kpis.revenue_this_month)}
                sub={`Last month: ${fmtMoney(analytics.kpis.revenue_last_month)}`}
                trend={analytics.kpis.revenue_change_pct} />
              <KpiCard icon="✅" label="Completion Rate" color="#059669"
                value={analytics.kpis.completion_rate + '%'}
                sub={`${analytics.kpis.wos_completed} of ${analytics.kpis.wos_scheduled} WOs`} />
              <KpiCard icon="🏢" label="Active Clients" color={GOLD}
                value={analytics.kpis.active_clients}
                sub={`${analytics.kpis.total_clients} total clients`} />
              <KpiCard icon="💳" label="Pending A/R" color="#DC2626"
                value={fmtMoney(analytics.kpis.pending_ar)}
                sub="Outstanding invoices" />
            </div>

            {/* Row 2 — 2 charts */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
              {/* Revenue by month */}
              <div style={{ background: 'white', borderRadius: '10px', padding: '20px',
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                <h3 style={{ margin: '0 0 16px', fontSize: '14px', fontWeight: '700', color: '#111827' }}>
                  💰 Revenue by Month
                </h3>
                <BarChart data={analytics.revenue_by_month} valueKey="revenue" labelKey="month"
                  color={GREEN} height={100} formatValue={v => '$' + Math.round(v/1000*10)/10 + 'k'} />
              </div>

              {/* WOs by month */}
              <div style={{ background: 'white', borderRadius: '10px', padding: '20px',
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                <h3 style={{ margin: '0 0 16px', fontSize: '14px', fontWeight: '700', color: '#111827' }}>
                  📋 Work Orders by Month
                </h3>
                <BarChart data={analytics.wos_by_month} valueKey="total" labelKey="month"
                  color="#3B82F6" height={100} />
                <div style={{ display: 'flex', gap: '16px', marginTop: '8px', justifyContent: 'center' }}>
                  <span style={{ fontSize: '12px', color: '#6B7280' }}>
                    <span style={{ display: 'inline-block', width: '10px', height: '10px', background: '#3B82F6', borderRadius: '2px', marginRight: '4px' }}/>Total
                  </span>
                  <span style={{ fontSize: '12px', color: '#6B7280' }}>
                    <span style={{ display: 'inline-block', width: '10px', height: '10px', background: GREEN, borderRadius: '2px', marginRight: '4px' }}/>Completed: {analytics.wos_by_month.reduce((s,m) => s + m.completed, 0)}
                  </span>
                </div>
              </div>
            </div>

            {/* Row 3 — Top technicians + Top clients + Device activity */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '28px' }}>
              {/* Top technicians */}
              <div style={{ background: 'white', borderRadius: '10px', padding: '20px',
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                <h3 style={{ margin: '0 0 16px', fontSize: '14px', fontWeight: '700', color: '#111827' }}>
                  👷 Top Technicians (YTD)
                </h3>
                {analytics.top_technicians.length === 0 ? (
                  <p style={{ color: '#9CA3AF', fontSize: '13px', textAlign: 'center', padding: '20px 0' }}>No completed WOs yet</p>
                ) : analytics.top_technicians.map((t, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', padding: '10px 0',
                    borderBottom: i < analytics.top_technicians.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ width: '24px', height: '24px', borderRadius: '50%', background: GREEN,
                        color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '11px', fontWeight: '700', flexShrink: 0 }}>{i+1}</span>
                      <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>{t.name}</span>
                    </div>
                    <span style={{ fontSize: '13px', fontWeight: '700', color: GREEN }}>
                      {t.completed_wos} WOs
                    </span>
                  </div>
                ))}
              </div>

              {/* Top clients */}
              <div style={{ background: 'white', borderRadius: '10px', padding: '20px',
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                <h3 style={{ margin: '0 0 16px', fontSize: '14px', fontWeight: '700', color: '#111827' }}>
                  🏢 Top Clients by Revenue (YTD)
                </h3>
                {analytics.top_clients.filter(c => c.revenue > 0).length === 0 ? (
                  <p style={{ color: '#9CA3AF', fontSize: '13px', textAlign: 'center', padding: '20px 0' }}>No revenue data yet</p>
                ) : analytics.top_clients.filter(c => c.revenue > 0).map((c, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between',
                    alignItems: 'center', padding: '10px 0',
                    borderBottom: i < analytics.top_clients.filter(x => x.revenue > 0).length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '140px' }}>
                      {c.client_name}
                    </span>
                    <span style={{ fontSize: '13px', fontWeight: '700', color: GOLD }}>
                      {fmtMoney(c.revenue)}
                    </span>
                  </div>
                ))}
              </div>

              {/* Device activity */}
              <div style={{ background: 'white', borderRadius: '10px', padding: '20px',
                boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                <h3 style={{ margin: '0 0 16px', fontSize: '14px', fontWeight: '700', color: '#111827' }}>
                  📍 Device Activity (Last 30 Days)
                </h3>
                <div style={{ textAlign: 'center', padding: '8px 0' }}>
                  {/* Donut-style display */}
                  <div style={{ fontSize: '48px', fontWeight: '800', color: analytics.device_activity.activity_rate > 0 ? '#DC2626' : '#9CA3AF' }}>
                    {analytics.device_activity.activity_rate}%
                  </div>
                  <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '16px' }}>Activity Rate</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    {[
                      { label: 'Total Inspections', value: analytics.device_activity.total_inspections, color: '#3B82F6' },
                      { label: 'With Activity',     value: analytics.device_activity.with_activity,     color: '#DC2626' },
                      { label: 'Unique Devices',    value: analytics.device_activity.unique_devices,    color: GREEN },
                    ].map(({ label, value, color }) => (
                      <div key={label} style={{ background: '#F9FAFB', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
                        <div style={{ fontSize: '20px', fontWeight: '700', color }}>{value}</div>
                        <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '2px' }}>{label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* ── DIVIDER ── */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <div style={{ flex: 1, height: '1px', background: '#E5E7EB' }} />
          <span style={{ fontSize: '13px', fontWeight: '700', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            📋 Operational Reports
          </span>
          <div style={{ flex: 1, height: '1px', background: '#E5E7EB' }} />
        </div>

        {REPORT_CATALOG.map(sec => (
          <div key={sec.section} style={s.section}>
            <div style={s.sectionHeader}>{sec.icon} {sec.section}</div>
            <div style={s.cardGrid}>
              {sec.reports.map(r => (
                <div key={r.id}
                  style={{ ...s.card, ...(r.available ? s.cardAvail : s.cardSoon) }}
                  onClick={() => openReport(r)}>
                  <div style={s.cardIcon}>{r.icon}</div>
                  <div style={s.cardLabel}>{r.label}</div>
                  <div style={s.cardDesc}>{r.desc}</div>
                  {!r.available && <div style={s.badge}>Coming Soon</div>}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Layout>
  );

  const reportMeta = REPORT_CATALOG.flatMap(s => s.reports).find(r => r.id === activeReport);

  /* ── REPORT VIEW ── */
  return (
    <Layout>
      <div style={s.page}>
        {/* Header */}
        <div style={s.reportHeader}>
          <button style={s.backBtn} onClick={() => { setActiveReport(null); navigate('/reports', {replace:true}); }}>
            ← Back
          </button>
          <div>
            <h1 style={s.pageTitle}>{reportMeta?.icon} {reportMeta?.label}</h1>
          </div>
        </div>

        {/* Filters */}
        <div style={s.filterBar}>
          {(activeReport === 'chemical-log' || activeReport === 'service-history') && (
            <select style={s.sel} value={filter.facility_id} onChange={e => setFilter(f=>({...f,facility_id:e.target.value}))}>
              <option value="">All Facilities</option>
              {filters.facilities.map(f => <option key={f.id} value={f.id}>{f.client_name} — {f.name}</option>)}
            </select>
          )}
          {activeReport === 'invoice-register' && (
            <select style={s.sel} value={filter.client_id} onChange={e => setFilter(f=>({...f,client_id:e.target.value}))}>
              <option value="">All Clients</option>
              {filters.clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          )}
          {activeReport === 'service-history' && (
            <>
              <select style={s.sel} value={filter.technician_id} onChange={e => setFilter(f=>({...f,technician_id:e.target.value}))}>
                <option value="">All Technicians</option>
                {filters.technicians.map(t => <option key={t.id} value={t.id}>{t.first_name} {t.last_name}</option>)}
              </select>
              <select style={s.sel} value={filter.status} onChange={e => setFilter(f=>({...f,status:e.target.value}))}>
                <option value="">All Statuses</option>
                {['pending','scheduled','in_progress','completed','cancelled'].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </>
          )}
          {activeReport === 'invoice-register' && (
            <select style={s.sel} value={filter.status} onChange={e => setFilter(f=>({...f,status:e.target.value}))}>
              <option value="">All Statuses</option>
              {['pending','sent','paid','overdue','cancelled'].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
          {activeReport === 'chemical-log' && (
            <select style={s.sel} value={filter.user_id} onChange={e => setFilter(f=>({...f,user_id:e.target.value}))}>
              <option value="">All Technicians</option>
              {filters.technicians.map(t => <option key={t.id} value={t.id}>{t.first_name} {t.last_name}</option>)}
            </select>
          )}
          {activeReport === 'tech-production' && (
            <select style={s.sel} value={filter.technician_id} onChange={e => setFilter(f=>({...f,technician_id:e.target.value}))}>
              <option value="">All Technicians</option>
              {filters.technicians.map(t => <option key={t.id} value={t.id}>{t.first_name} {t.last_name}</option>)}
            </select>
          )}
          {activeReport === 'ar' && (
            <>
              <select style={s.sel} value={filter.client_id} onChange={e => setFilter(f=>({...f,client_id:e.target.value}))}>
                <option value="">All Clients</option>
                {filters.clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <input style={s.inp} type="date" value={filter.end_date}
                onChange={e => setFilter(f=>({...f,end_date:e.target.value}))}
                title="As of date" />
              <span style={{fontSize:'12px',color:'#888',alignSelf:'center'}}>As of date</span>
            </>
          )}
          {activeReport === 'pesticide-annual' && (
            <>
              <select style={{...s.sel, borderColor:'#2D5C3F', fontWeight:600}} value={filter.state} onChange={e => setFilter(f=>({...f,state:e.target.value}))}>
                {STATE_OPTIONS.map(st => <option key={st} value={st}>{STATE_FORMATS[st].label}</option>)}
              </select>
              <select style={s.sel} value={filter.year} onChange={e => setFilter(f=>({...f,year:e.target.value}))}>
                {YEAR_OPTIONS.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              <select style={s.sel} value={filter.user_id} onChange={e => setFilter(f=>({...f,user_id:e.target.value}))}>
                <option value="">All Applicators</option>
                {filters.technicians.map(t => <option key={t.id} value={t.id}>{t.first_name} {t.last_name}</option>)}
              </select>
            </>
          )}
          {activeReport !== 'pesticide-annual' && (<>
          <input style={s.inp} type="date" value={filter.start_date} onChange={e => setFilter(f=>({...f,start_date:e.target.value}))} placeholder="Start date" />
          <input style={s.inp} type="date" value={filter.end_date}   onChange={e => setFilter(f=>({...f,end_date:e.target.value}))}   placeholder="End date" />
          </>)}
          <button style={s.runBtn} onClick={() => runReport(activeReport, filter)}>
            {loading ? '...' : '▶ Run'}
          </button>
          <button style={s.clearBtn} onClick={() => { setFilter({facility_id:'',start_date:'',end_date:'',status:'',technician_id:'',client_id:'',user_id:'',year:String(new Date().getFullYear())}); }}>
            Clear
          </button>
          {data.length > 0 && (<>
            <button style={s.csvBtn} onClick={() => exportCSV(activeReport, activeReport === 'pesticide-annual' ? data.flatMap(a => a.applications.map(app => ({...app, applicator_name: a.applicator_name, ny_license: a.ny_license}))) : activeReport === 'ar' ? data.flatMap(c => c.invoices) : activeReport === 'tech-production' ? data.filter(t=>t.services&&t.services.length>0).flatMap(t=>t.services.map(s=>({...s,technician:t.first_name+' '+t.last_name}))) : data)}>
              ⬇ CSV
            </button>
            {activeReport === 'ar'
              ? <button style={s.printBtn} onClick={() => printAR(data, totals, filter.end_date || new Date().toLocaleDateString('en-US'))}>🖨️ Print</button>
              : activeReport === 'tech-production'
              ? <button style={s.printBtn} onClick={() => printTechProduction(data, totals, (filter.start_date||'') + (filter.start_date&&filter.end_date?' – ':'') + (filter.end_date||''))}>🖨️ Print</button>
              : activeReport === 'pesticide-annual'
              ? <button style={s.printBtn} onClick={() => printPesticideAnnual(data, filter)}>🖨️ Print</button>
              : <button style={s.printBtn} onClick={() => printReport(activeReport, data, totals, reportMeta)}>🖨️ Print</button>
            }
          </>)}
        </div>

        {/* Summary cards for invoice */}
        {totals && activeReport === 'invoice-register' && (
          <div style={s.kpiRow}>
            {[
              { label:'Total Billed',   val: fmtMoney(totals.total_billed),   color:'#1565c0' },
              { label:'Total Paid',     val: fmtMoney(totals.total_paid),     color:'#2e7d32' },
              { label:'Pending',        val: fmtMoney(totals.total_pending),  color:'#f57c00' },
              { label:'Overdue',        val: fmtMoney(totals.total_overdue),  color:'#c62828' },
            ].map(k => (
              <div key={k.label} style={s.kpiCard}>
                <div style={{ ...s.kpiVal, color: k.color }}>{k.val}</div>
                <div style={s.kpiLabel}>{k.label}</div>
              </div>
            ))}
          </div>
        )}
        {totals && activeReport === 'tech-production' && (
          <div style={s.kpiRow}>
            {[
              { label:'Total Assigned', val: totals.total_assigned,                color:'#1565c0' },
              { label:'Completed',      val: totals.completed,                     color:'#2e7d32' },
              { label:'Cancelled',      val: totals.cancelled,                     color:'#c62828' },
              { label:'Total Revenue',  val: fmtMoney(totals.revenue),             color:'#2D5C3F' },
            ].map(k => (
              <div key={k.label} style={s.kpiCard}>
                <div style={{ ...s.kpiVal, color: k.color }}>{k.val}</div>
                <div style={s.kpiLabel}>{k.label}</div>
              </div>
            ))}
          </div>
        )}
        {totals && activeReport === 'ar' && (
          <div style={s.kpiRow}>
            {[
              { label:'Current',      val: fmtMoney(totals.current),     color:'#1565c0' },
              { label:'1-30 Days',    val: fmtMoney(totals.days_1_30),   color:'#f57c00' },
              { label:'31-60 Days',   val: fmtMoney(totals.days_31_60),  color:'#e65100' },
              { label:'61-90 Days',   val: fmtMoney(totals.days_61_90),  color:'#b71c1c' },
              { label:'90+ Days',     val: fmtMoney(totals.days_90plus), color:'#880e4f' },
              { label:'Total AR',     val: fmtMoney(totals.total),       color:'#2D5C3F' },
            ].map(k => (
              <div key={k.label} style={s.kpiCard}>
                <div style={{ ...s.kpiVal, color: k.color }}>{k.val}</div>
                <div style={s.kpiLabel}>{k.label}</div>
              </div>
            ))}
          </div>
        )}

        {/* Record count */}
        {!loading && data.length > 0 && (
          <div style={s.countBar}>
            {activeReport === 'pesticide-annual'
              ? data.reduce((a,ap) => a + ap.applications.length, 0) + ' applications across ' + data.length + ' applicator' + (data.length !== 1 ? 's' : '')
              : activeReport === 'ar'
              ? data.length + ' client' + (data.length !== 1 ? 's' : '') + ' with outstanding balance'
              : data.length + ' record' + (data.length !== 1 ? 's' : '')}
          </div>
        )}

        {loading && <div style={s.loading}>Loading...</div>}

        {/* Tables */}
        {!loading && activeReport === 'chemical-log' && data.length > 0 && (
          <div style={s.tableWrap}>
            <table style={s.table}>
              <thead>
                <tr>
                  {['Date / Time','Facility','Client','Product','EPA #','Active Ingredient','Method','Qty','Dilution','Area','Technician','WO #'].map(h => (
                    <th key={h} style={s.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((r,i) => (
                  <tr key={r.id} style={i%2===0 ? s.trEven : s.trOdd}>
                    <td style={s.td}>{fmtDT(r.applied_at)}</td>
                    <td style={s.td}>{r.facility_name}</td>
                    <td style={s.td}>{r.client_name}</td>
                    <td style={{...s.td, fontWeight:600}}>{r.product_name}</td>
                    <td style={s.td}>{r.epa_number || '—'}</td>
                    <td style={s.td}>{r.active_ingredient || '—'}</td>
                    <td style={s.td}>{METHOD_LABELS[r.application_method] || r.application_method || '—'}</td>
                    <td style={s.td}>{r.quantity} {r.unit}</td>
                    <td style={s.td}>{r.dilution_rate || (r.dilution_oz_per_gallon ? r.dilution_oz_per_gallon + ' oz/gal' : '—')}</td>
                    <td style={s.td}>{r.area_name || '—'}</td>
                    <td style={s.td}>{r.tech_first} {r.tech_last}</td>
                    <td style={s.td}>{r.order_number}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && activeReport === 'service-history' && data.length > 0 && (
          <div style={s.tableWrap}>
            <table style={s.table}>
              <thead>
                <tr>
                  {['WO #','Date','Facility','Client','Type','Status','Scheduled','Actual','Technician','Price','Invoice'].map(h => (
                    <th key={h} style={s.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((r,i) => (
                  <tr key={r.id} style={i%2===0 ? s.trEven : s.trOdd}>
                    <td style={{...s.td, fontWeight:600}}>{r.order_number}</td>
                    <td style={s.td}>{fmtDate(r.scheduled_date)}</td>
                    <td style={s.td}>{r.facility_name}</td>
                    <td style={s.td}>{r.client_name}</td>
                    <td style={s.td}>{r.service_type}</td>
                    <td style={s.td}>
                      <span style={{ ...s.statusBadge, ...statusColor(r.status) }}>{r.status}</span>
                    </td>
                    <td style={s.td}>{fmtTime(r.scheduled_time_start)} – {fmtTime(r.scheduled_time_end)}</td>
                    <td style={s.td}>{fmtMin(r.actual_minutes)}</td>
                    <td style={s.td}>{r.tech_first} {r.tech_last}</td>
                    <td style={s.td}>{fmtMoney(r.service_price)}</td>
                    <td style={s.td}>
                      <span style={{ ...s.statusBadge, ...statusColor(r.invoice_status) }}>{r.invoice_status || '—'}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && activeReport === 'invoice-register' && data.length > 0 && (
          <div style={s.tableWrap}>
            <table style={s.table}>
              <thead>
                <tr>
                  {['Invoice #','Date','Due Date','Client','Facility','WO #','Status','Total','Paid','Balance','Paid On','Method','PDF'].map(h => (
                    <th key={h} style={s.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((r,i) => {
                  const balance = parseFloat(r.total_amount||0) - parseFloat(r.paid_amount||0);
                  return (
                    <tr key={r.id} style={i%2===0 ? s.trEven : s.trOdd}>
                      <td style={{...s.td, fontWeight:600}}>{r.invoice_number}</td>
                      <td style={s.td}>{fmtDate(r.invoice_date)}</td>
                      <td style={s.td}>{fmtDate(r.due_date)}</td>
                      <td style={s.td}>{r.client_name}</td>
                      <td style={s.td}>{r.facility_name || '—'}</td>
                      <td style={s.td}>{r.order_number || '—'}</td>
                      <td style={s.td}>
                        <span style={{ ...s.statusBadge, ...statusColor(r.status) }}>{r.status}</span>
                      </td>
                      <td style={s.td}>{fmtMoney(r.total_amount)}</td>
                      <td style={s.td}>{fmtMoney(r.paid_amount)}</td>
                      <td style={{ ...s.td, color: balance > 0 ? '#c62828' : '#2e7d32', fontWeight:600 }}>{fmtMoney(balance)}</td>
                      <td style={s.td}>{fmtDate(r.paid_at)}</td>
                      <td style={s.td}>{r.payment_method || '—'}</td>
                      <td style={s.td}>
                        <button onClick={() => openInvoicePdf(r.id, r.invoice_number, setInvoicePdf, setInvoicePdfLoading)}
                          style={{ padding:'4px 10px', background:'#2D5C3F', color:'white', border:'none',
                            borderRadius:'5px', fontSize:'11px', fontWeight:'600', cursor:'pointer' }}>
                          📄 PDF
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loading && activeReport === 'tech-production' && data.length > 0 && (
          <div>
            {/* Summary table */}
            <div style={s.tableWrap}>
              <table style={s.table}>
                <thead>
                  <tr>
                    {['Technician','Assigned','Completed','Cancelled','In Progress','Completion %','Avg Time','Sched Time','Revenue','Facilities','Period'].map(h => (
                      <th key={h} style={{...s.th, textAlign: ['Assigned','Completed','Cancelled','In Progress','Completion %','Avg Time','Sched Time','Revenue','Facilities'].includes(h)?'right':'left'}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.filter(t => parseInt(t.total_assigned) > 0 || parseInt(t.completed) > 0).map((t,i) => (
                    <tr key={t.technician_id} style={i%2===0 ? s.trEven : s.trOdd}>
                      <td style={{...s.td, fontWeight:700}}>{t.first_name} {t.last_name}</td>
                      <td style={{...s.td, textAlign:'right'}}>{t.total_assigned}</td>
                      <td style={{...s.td, textAlign:'right', color:'#2e7d32', fontWeight:600}}>{t.completed}</td>
                      <td style={{...s.td, textAlign:'right', color: parseInt(t.cancelled)>0?'#c62828':'#aaa'}}>{t.cancelled}</td>
                      <td style={{...s.td, textAlign:'right', color: parseInt(t.in_progress)>0?'#f57c00':'#aaa'}}>{t.in_progress}</td>
                      <td style={{...s.td, textAlign:'right'}}>
                        <span style={{...s.statusBadge, background: parseFloat(t.completion_rate)>=80?'#e8f5e9':parseFloat(t.completion_rate)>=50?'#fff8e1':'#ffebee', color: parseFloat(t.completion_rate)>=80?'#2e7d32':parseFloat(t.completion_rate)>=50?'#f57c00':'#c62828'}}>
                          {t.completion_rate}%
                        </span>
                      </td>
                      <td style={{...s.td, textAlign:'right'}}>{t.avg_actual_min ? t.avg_actual_min + ' min' : '—'}</td>
                      <td style={{...s.td, textAlign:'right'}}>{t.avg_scheduled_min ? t.avg_scheduled_min + ' min' : '—'}</td>
                      <td style={{...s.td, textAlign:'right', fontWeight:600, color:'#2D5C3F'}}>{fmtMoney(t.revenue)}</td>
                      <td style={{...s.td, textAlign:'right'}}>{t.unique_facilities}</td>
                      <td style={s.td}>{t.first_service ? fmtDate(t.first_service) + ' – ' + fmtDate(t.last_service) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Detail per technician */}
            <div style={{marginTop:'28px'}}>
              <div style={{fontSize:'15px', fontWeight:700, color:'#2D5C3F', marginBottom:'14px'}}>Service Detail</div>
              {data.filter(t => t.services && t.services.length > 0).map(t => (
                <div key={t.technician_id} style={{marginBottom:'24px'}}>
                  <div style={{fontWeight:700, color:'#2D5C3F', fontSize:'13px', marginBottom:'6px', padding:'6px 10px', background:'#f0f4f1', borderRadius:'6px', display:'flex', justifyContent:'space-between'}}>
                    <span>{t.first_name} {t.last_name}</span>
                    <span style={{fontWeight:400, fontSize:'12px', color:'#555'}}>{t.completed} completed · {t.total_assigned} total · {fmtMoney(t.revenue)}</span>
                  </div>
                  <div style={s.tableWrap}>
                    <table style={s.table}>
                      <thead>
                        <tr>
                          {['WO #','Date','Client','Facility','Type','Status','Sched Time','Actual Min','Price','Invoice'].map(h => (
                            <th key={h} style={s.th}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {t.services.map((sv,i) => (
                          <tr key={sv.id} style={i%2===0 ? s.trEven : s.trOdd}>
                            <td style={{...s.td, fontWeight:600}}>{sv.order_number}</td>
                            <td style={s.td}>{fmtDate(sv.scheduled_date)}</td>
                            <td style={s.td}>{sv.client_name}</td>
                            <td style={s.td}>{sv.facility_name}</td>
                            <td style={s.td}>{sv.service_type}</td>
                            <td style={s.td}><span style={{...s.statusBadge, ...statusColor(sv.status)}}>{sv.status}</span></td>
                            <td style={s.td}>{sv.scheduled_date}</td>
                            <td style={s.td}>{sv.actual_min ? Math.round(sv.actual_min) + ' min' : '—'}</td>
                            <td style={s.td}>{fmtMoney(sv.service_price)}</td>
                            <td style={s.td}><span style={{...s.statusBadge, ...statusColor(sv.invoice_status)}}>{sv.invoice_status || '—'}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {!loading && activeReport === 'pesticide-annual' && data.length > 0 && (
          <div>
            {data.map(applicator => (
              <div key={applicator.applicator_id} style={s.nySection}>
                {/* Applicator header */}
                <div style={s.nyApplicatorHeader}>
                  <div style={s.nyApplicatorLeft}>
                    <div style={s.nyApplicatorName}>{applicator.applicator_name}</div>
                    <div style={s.nyApplicatorMeta}>
                      {filter.state || 'State'} License: <strong>{applicator.state_license || applicator.ny_license}</strong>
                      {(applicator.state_license_type || applicator.ny_license_type) && <span> — {applicator.state_license_type || applicator.ny_license_type}</span>}
                      {applicator.license_expiry && <span style={{color:'#f57c00'}}> | Expires: {fmtDate(applicator.license_expiry)}</span>}
                    </div>
                    <div style={s.nyApplicatorMeta}>
                      {applicator.company_name} | {applicator.company_address}, {applicator.company_city}, {applicator.company_state} {applicator.company_zip}
                    </div>
                  </div>
                  <div style={s.nyApplicatorRight}>
                    <div style={s.nyBadge}>{applicator.applications.length} Applications</div>
                  </div>
                </div>
                {/* Applications table */}
                <div style={s.tableWrap}>
                  <table style={s.table}>
                    <thead>
                      <tr>
                        {['Date','Site / Facility','Client','Product','EPA Reg #','Active Ingredient','Method','Qty','Dilution','Area','WO #'].map(h => (
                          <th key={h} style={s.th}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {applicator.applications.map((r,i) => (
                        <tr key={r.id} style={i%2===0 ? s.trEven : s.trOdd}>
                          <td style={s.td}>{fmtDT(r.applied_at)}</td>
                          <td style={s.td}>{r.facility_name}<br/><span style={{fontSize:'11px',color:'#888'}}>{r.facility_address}, {r.city} {r.state}</span></td>
                          <td style={s.td}>{r.client_name}</td>
                          <td style={{...s.td, fontWeight:600}}>{r.product_name}</td>
                          <td style={s.td}>{r.epa_number || '—'}</td>
                          <td style={s.td}>{r.active_ingredient || '—'}{r.ai_percentage ? <span style={{color:'#888'}}> ({r.ai_percentage}%)</span> : ''}</td>
                          <td style={s.td}>{METHOD_LABELS[r.application_method] || r.application_method || '—'}</td>
                          <td style={s.td}>{r.quantity} {r.unit}</td>
                          <td style={s.td}>{r.dilution_rate || (r.dilution_oz_per_gallon ? r.dilution_oz_per_gallon+' oz/gal' : '—')}</td>
                          <td style={s.td}>{r.area_name || '—'}</td>
                          <td style={s.td}>{r.order_number}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && activeReport === 'ar' && data.length > 0 && (
          <div>
            {/* Aging Summary Table */}
            <div style={s.tableWrap}>
              <table style={s.table}>
                <thead>
                  <tr>
                    {['Client','Current','1-30 Days','31-60 Days','61-90 Days','90+ Days','Total Balance'].map(h => (
                      <th key={h} style={{...s.th, textAlign: h==='Client' ? 'left':'right'}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.map((c,i) => (
                    <tr key={c.client_id} style={i%2===0 ? s.trEven : s.trOdd}>
                      <td style={{...s.td, fontWeight:600}}>{c.client_name}</td>
                      <td style={{...s.td, textAlign:'right', color: c.current>0?'#1565c0':'#aaa'}}>{c.current>0 ? fmtMoney(c.current) : '—'}</td>
                      <td style={{...s.td, textAlign:'right', color: c.days_1_30>0?'#f57c00':'#aaa'}}>{c.days_1_30>0 ? fmtMoney(c.days_1_30) : '—'}</td>
                      <td style={{...s.td, textAlign:'right', color: c.days_31_60>0?'#e65100':'#aaa'}}>{c.days_31_60>0 ? fmtMoney(c.days_31_60) : '—'}</td>
                      <td style={{...s.td, textAlign:'right', color: c.days_61_90>0?'#b71c1c':'#aaa'}}>{c.days_61_90>0 ? fmtMoney(c.days_61_90) : '—'}</td>
                      <td style={{...s.td, textAlign:'right', color: c.days_90plus>0?'#880e4f':'#aaa', fontWeight: c.days_90plus>0?700:400}}>{c.days_90plus>0 ? fmtMoney(c.days_90plus) : '—'}</td>
                      <td style={{...s.td, textAlign:'right', fontWeight:700}}>{fmtMoney(c.total)}</td>
                    </tr>
                  ))}
                  {/* Totals row */}
                  <tr style={{background:'#f0f4f1', fontWeight:700}}>
                    <td style={{...s.td, fontWeight:700, color:'#2D5C3F'}}>TOTAL</td>
                    <td style={{...s.td, textAlign:'right'}}>{fmtMoney(totals?.current)}</td>
                    <td style={{...s.td, textAlign:'right'}}>{fmtMoney(totals?.days_1_30)}</td>
                    <td style={{...s.td, textAlign:'right'}}>{fmtMoney(totals?.days_31_60)}</td>
                    <td style={{...s.td, textAlign:'right'}}>{fmtMoney(totals?.days_61_90)}</td>
                    <td style={{...s.td, textAlign:'right'}}>{fmtMoney(totals?.days_90plus)}</td>
                    <td style={{...s.td, textAlign:'right', color:'#2D5C3F'}}>{fmtMoney(totals?.total)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Detail per client */}
            <div style={{marginTop:'28px'}}>
              <div style={{fontSize:'15px', fontWeight:700, color:'#2D5C3F', marginBottom:'14px'}}>Invoice Detail</div>
              {data.map(c => (
                <div key={c.client_id} style={{marginBottom:'20px'}}>
                  <div style={{fontWeight:700, color:'#2D5C3F', fontSize:'13px', marginBottom:'6px', padding:'6px 10px', background:'#f0f4f1', borderRadius:'6px'}}>
                    {c.client_name} — Balance: {fmtMoney(c.total)}
                  </div>
                  <div style={s.tableWrap}>
                    <table style={s.table}>
                      <thead>
                        <tr>
                          {['Invoice #','Date','Due Date','Facility','WO #','Status','Total','Paid','Balance','Age (days)','Bucket'].map(h => (
                            <th key={h} style={s.th}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {c.invoices.map((inv,i) => (
                          <tr key={inv.id} style={i%2===0 ? s.trEven : s.trOdd}>
                            <td style={{...s.td, fontWeight:600}}>{inv.invoice_number}</td>
                            <td style={s.td}>{fmtDate(inv.invoice_date)}</td>
                            <td style={s.td}>{fmtDate(inv.due_date)}</td>
                            <td style={s.td}>{inv.facility_name || '—'}</td>
                            <td style={s.td}>{inv.order_number || '—'}</td>
                            <td style={s.td}><span style={{...s.statusBadge, ...statusColor(inv.status)}}>{inv.status}</span></td>
                            <td style={s.td}>{fmtMoney(inv.total_amount)}</td>
                            <td style={s.td}>{fmtMoney(inv.paid_amount)}</td>
                            <td style={{...s.td, fontWeight:700, color: inv.days_past_due>30?'#c62828':'#333'}}>{fmtMoney(inv.balance)}</td>
                            <td style={{...s.td, color: inv.days_past_due>0?'#c62828':'#2e7d32', fontWeight:600}}>{inv.days_past_due > 0 ? inv.days_past_due + 'd' : 'Current'}</td>
                            <td style={s.td}><span style={{...s.statusBadge, background:'#eee', color:'#555'}}>{inv.bucket?.replace(/_/g,' ')}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {!loading && activeReport === 'ar' && data.length === 0 && (
          <div style={s.empty}>No outstanding invoices found.</div>
        )}

        {!loading && activeReport === 'ar' && (
          <></>
        )}

        {!loading && activeReport !== 'ar' && data.length === 0 && activeReport && (
          <div style={s.empty}>No records found. Adjust filters and click ▶ Run.</div>
        )}
      </div>
    {/* INVOICE PDF MODAL */}
    {invoicePdf && (
      <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', zIndex:2000,
        display:'flex', alignItems:'center', justifyContent:'center', padding:'20px' }}
        onClick={e => { if(e.target===e.currentTarget){ if(invoicePdf.url) URL.revokeObjectURL(invoicePdf.url); setInvoicePdf(null); }}}>
        <div style={{ background:'#FFF', borderRadius:'12px', width:'100%', maxWidth:'900px',
          height:'90vh', display:'flex', flexDirection:'column', overflow:'hidden',
          boxShadow:'0 25px 60px rgba(0,0,0,0.4)' }}>
          <div style={{ padding:'16px 24px', background:'linear-gradient(135deg, #1F4027, #2D5C3F)',
            display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
            <div>
              <h2 style={{ margin:0, color:'#FFF', fontSize:'17px', fontWeight:'700' }}>Invoice PDF</h2>
              <p style={{ margin:'3px 0 0', color:'#D4AF37', fontSize:'13px' }}>{invoicePdf.invoice_number}</p>
            </div>
            <div style={{ display:'flex', gap:'8px' }}>
              {invoicePdf.url && (
                <a href={invoicePdf.url} download={`Invoice_${invoicePdf.invoice_number}.pdf`}
                  style={{ background:'#D4AF37', color:'#1A3A2A', padding:'8px 16px', borderRadius:'6px',
                    fontSize:'13px', fontWeight:'700', textDecoration:'none' }}>
                  ⬇ Download
                </a>
              )}
              <button onClick={() => { if(invoicePdf.url) URL.revokeObjectURL(invoicePdf.url); setInvoicePdf(null); }}
                style={{ background:'rgba(255,255,255,0.15)', border:'1px solid rgba(255,255,255,0.3)',
                  color:'#FFF', padding:'8px 16px', borderRadius:'6px', cursor:'pointer', fontSize:'14px', fontWeight:'600' }}>
                ✕ Close
              </button>
            </div>
          </div>
          <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden' }}>
            {invoicePdfLoading ? (
              <div style={{ display:'flex', flexDirection:'column', alignItems:'center',
                justifyContent:'center', height:'100%', gap:'16px' }}>
                <div style={{ width:'44px', height:'44px', border:'4px solid #E5E7EB',
                  borderTopColor:'#2D5C3F', borderRadius:'50%', animation:'spin 1s linear infinite' }} />
                <p style={{ color:'#6B7280', fontSize:'14px' }}>Generating invoice PDF...</p>
              </div>
            ) : invoicePdf.url ? (
              <iframe src={invoicePdf.url} style={{ flex:1, border:'none', width:'100%', height:'100%' }}
                title="Invoice PDF" />
            ) : null}
          </div>
        </div>
      </div>
    )}
    </Layout>
  );
}

function printAR(data, totals, asOfDate) {
  if (!data || !data.length) return;
  const fmtM = (n) => n != null ? '$' + parseFloat(n).toFixed(2) : '—';
  const fmtD = (d) => d ? new Date(d+'T12:00:00').toLocaleDateString('en-US',{month:'short',day:'2-digit',year:'numeric'}) : '—';

  const summaryRows = data.map((c,i) => `
    <tr class="${i%2===0?'even':'odd'}">
      <td style="font-weight:600">${c.client_name}</td>
      <td class="num" style="color:#1565c0">${c.current>0?fmtM(c.current):'—'}</td>
      <td class="num" style="color:${c.days_1_30>0?'#f57c00':'#aaa'}">${c.days_1_30>0?fmtM(c.days_1_30):'—'}</td>
      <td class="num" style="color:${c.days_31_60>0?'#e65100':'#aaa'}">${c.days_31_60>0?fmtM(c.days_31_60):'—'}</td>
      <td class="num" style="color:${c.days_61_90>0?'#b71c1c':'#aaa'}">${c.days_61_90>0?fmtM(c.days_61_90):'—'}</td>
      <td class="num" style="color:${c.days_90plus>0?'#880e4f':'#aaa'};font-weight:${c.days_90plus>0?700:400}">${c.days_90plus>0?fmtM(c.days_90plus):'—'}</td>
      <td class="num" style="font-weight:700">${fmtM(c.total)}</td>
    </tr>`).join('');

  const detailSections = data.map(c => {
    const invRows = c.invoices.map((inv,i) => `
      <tr class="${i%2===0?'even':'odd'}">
        <td style="font-weight:600">${inv.invoice_number}</td>
        <td>${fmtD(inv.invoice_date)}</td>
        <td>${fmtD(inv.due_date)}</td>
        <td>${inv.facility_name||'—'}</td>
        <td>${inv.order_number||'—'}</td>
        <td>${inv.status}</td>
        <td class="num">${fmtM(inv.total_amount)}</td>
        <td class="num">${fmtM(inv.paid_amount)}</td>
        <td class="num" style="font-weight:700;color:${inv.days_past_due>30?'#c62828':'#333'}">${fmtM(inv.balance)}</td>
        <td class="num" style="color:${inv.days_past_due>0?'#c62828':'#2e7d32'};font-weight:600">${inv.days_past_due>0?inv.days_past_due+'d':'Current'}</td>
      </tr>`).join('');
    return `
      <div class="client-section">
        <div class="client-header">${c.client_name} — Balance: ${fmtM(c.total)}</div>
        <table>
          <thead><tr>
            <th>Invoice #</th><th>Date</th><th>Due Date</th><th>Facility</th>
            <th>WO #</th><th>Status</th><th class="num">Total</th>
            <th class="num">Paid</th><th class="num">Balance</th><th class="num">Age</th>
          </tr></thead>
          <tbody>${invRows}</tbody>
        </table>
      </div>`;
  }).join('');

  const win = window.open('', '_blank');
  win.document.write(`<!DOCTYPE html><html><head>
    <title>Accounts Receivable Aging</title>
    <style>
      @page { size: landscape; margin: 12mm 10mm; }
      * { box-sizing:border-box; }
      body { font-family:Arial,sans-serif; font-size:9px; color:#222; margin:0; }
      .header { display:flex; justify-content:space-between; align-items:flex-end; margin-bottom:10px; border-bottom:2px solid #2D5C3F; padding-bottom:6px; }
      .header h2 { color:#2D5C3F; margin:0; font-size:14px; }
      .header .meta { color:#666; font-size:9px; text-align:right; }
      .kpis { display:flex; gap:12px; margin-bottom:12px; }
      .kpi { flex:1; border:1px solid #ddd; border-radius:4px; padding:6px 8px; text-align:center; }
      .kpi .val { font-size:12px; font-weight:700; }
      .kpi .lbl { font-size:8px; color:#888; margin-top:2px; }
      h3 { color:#2D5C3F; font-size:11px; margin:14px 0 6px; border-bottom:1px solid #D4AF37; padding-bottom:3px; }
      table { width:100%; border-collapse:collapse; margin-bottom:6px; }
      th { background:#2D5C3F; color:#fff; padding:4px 6px; text-align:left; font-size:8px; white-space:nowrap; }
      th.num { text-align:right; }
      td { padding:3px 6px; border-bottom:1px solid #eee; font-size:8px; vertical-align:top; }
      td.num { text-align:right; }
      tr.even td { background:#fff; }
      tr.odd  td { background:#f9fafb; }
      .total-row td { background:#f0f4f1!important; font-weight:700; }
      .client-section { margin-top:12px; page-break-inside:avoid; }
      .client-header { background:#f0f4f1; color:#2D5C3F; font-weight:700; font-size:10px; padding:5px 8px; border-radius:4px; margin-bottom:4px; }
      .footer { margin-top:10px; font-size:8px; color:#aaa; display:flex; justify-content:space-between; border-top:1px solid #eee; padding-top:6px; }
    </style>
  </head><body>
    <div class="header">
      <h2>💳 Accounts Receivable Aging</h2>
      <div class="meta">As of: ${asOfDate || new Date().toLocaleDateString('en-US')}<br/>Generated: ${new Date().toLocaleString('en-US')}</div>
    </div>
    <div class="kpis">
      <div class="kpi"><div class="val" style="color:#1565c0">${fmtM(totals?.current)}</div><div class="lbl">Current</div></div>
      <div class="kpi"><div class="val" style="color:#f57c00">${fmtM(totals?.days_1_30)}</div><div class="lbl">1-30 Days</div></div>
      <div class="kpi"><div class="val" style="color:#e65100">${fmtM(totals?.days_31_60)}</div><div class="lbl">31-60 Days</div></div>
      <div class="kpi"><div class="val" style="color:#b71c1c">${fmtM(totals?.days_61_90)}</div><div class="lbl">61-90 Days</div></div>
      <div class="kpi"><div class="val" style="color:#880e4f">${fmtM(totals?.days_90plus)}</div><div class="lbl">90+ Days</div></div>
      <div class="kpi"><div class="val" style="color:#2D5C3F">${fmtM(totals?.total)}</div><div class="lbl">Total AR</div></div>
    </div>
    <h3>Aging Summary</h3>
    <table>
      <thead><tr>
        <th>Client</th>
        <th class="num">Current</th><th class="num">1-30 Days</th>
        <th class="num">31-60 Days</th><th class="num">61-90 Days</th>
        <th class="num">90+ Days</th><th class="num">Total</th>
      </tr></thead>
      <tbody>
        ${summaryRows}
        <tr class="total-row">
          <td>TOTAL</td>
          <td class="num">${fmtM(totals?.current)}</td>
          <td class="num">${fmtM(totals?.days_1_30)}</td>
          <td class="num">${fmtM(totals?.days_31_60)}</td>
          <td class="num">${fmtM(totals?.days_61_90)}</td>
          <td class="num">${fmtM(totals?.days_90plus)}</td>
          <td class="num">${fmtM(totals?.total)}</td>
        </tr>
      </tbody>
    </table>
    <h3>Invoice Detail</h3>
    ${detailSections}
    <div class="footer">
      <span>SafeCore Pest Management</span>
      <span>Confidential — Internal Use Only</span>
    </div>
    <script>window.onload=function(){window.print();}<\/script>
  </body></html>`);
  win.document.close();
}

function printTechProduction(data, totals, dateRange) {
  if (!data || !data.length) return;
  const fmtM = (n) => n != null ? '$' + parseFloat(n).toFixed(2) : '—';
  const fmtD = (d) => d ? new Date(d+'T12:00:00').toLocaleDateString('en-US',{month:'short',day:'2-digit',year:'numeric'}) : '—';

  const summaryRows = data.filter(t => parseInt(t.total_assigned) > 0).map((t,i) => {
    const rate = parseFloat(t.completion_rate) || 0;
    const rateColor = rate >= 80 ? '#2e7d32' : rate >= 50 ? '#f57c00' : '#c62828';
    return `<tr class="${i%2===0?'even':'odd'}">
      <td style="font-weight:700">${t.first_name} ${t.last_name}</td>
      <td class="num">${t.total_assigned}</td>
      <td class="num" style="color:#2e7d32;font-weight:600">${t.completed}</td>
      <td class="num" style="color:${parseInt(t.cancelled)>0?'#c62828':'#aaa'}">${t.cancelled}</td>
      <td class="num" style="color:${parseInt(t.in_progress)>0?'#f57c00':'#aaa'}">${t.in_progress}</td>
      <td class="num" style="color:${rateColor};font-weight:700">${t.completion_rate}%</td>
      <td class="num">${t.avg_actual_min ? t.avg_actual_min+' min' : '—'}</td>
      <td class="num">${t.avg_scheduled_min ? t.avg_scheduled_min+' min' : '—'}</td>
      <td class="num" style="color:#2D5C3F;font-weight:700">${fmtM(t.revenue)}</td>
      <td class="num">${t.unique_facilities}</td>
      <td>${t.first_service ? fmtD(t.first_service)+' – '+fmtD(t.last_service) : '—'}</td>
    </tr>`;
  }).join('');

  const detailSections = data.filter(t => t.services && t.services.length > 0).map(t => {
    const rows = t.services.map((sv,i) => `
      <tr class="${i%2===0?'even':'odd'}">
        <td style="font-weight:600">${sv.order_number}</td>
        <td>${fmtD(sv.scheduled_date)}</td>
        <td>${sv.client_name}</td>
        <td>${sv.facility_name}</td>
        <td>${sv.service_type||'—'}</td>
        <td>${sv.status}</td>
        <td class="num">${sv.actual_min ? Math.round(sv.actual_min)+' min' : '—'}</td>
        <td class="num">${fmtM(sv.service_price)}</td>
        <td>${sv.invoice_status||'—'}</td>
      </tr>`).join('');
    return `
      <div class="tech-section">
        <div class="tech-header">
          <span>${t.first_name} ${t.last_name}</span>
          <span class="tech-meta">${t.completed} completed &nbsp;·&nbsp; ${t.total_assigned} total &nbsp;·&nbsp; ${fmtM(t.revenue)}</span>
        </div>
        <table>
          <thead><tr>
            <th>WO #</th><th>Date</th><th>Client</th><th>Facility</th>
            <th>Type</th><th>Status</th><th class="num">Actual Min</th>
            <th class="num">Price</th><th>Invoice</th>
          </tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </div>`;
  }).join('');

  const win = window.open('', '_blank');
  win.document.write(`<!DOCTYPE html><html><head>
    <title>Technician Production Report</title>
    <style>
      @page { size: landscape; margin: 12mm 10mm; }
      * { box-sizing:border-box; }
      body { font-family:Arial,sans-serif; font-size:9px; color:#222; margin:0; }
      .header { display:flex; justify-content:space-between; align-items:flex-end; margin-bottom:10px; border-bottom:2px solid #2D5C3F; padding-bottom:6px; }
      .header h2 { color:#2D5C3F; margin:0; font-size:14px; }
      .header .meta { color:#666; font-size:9px; text-align:right; }
      .kpis { display:flex; gap:12px; margin-bottom:12px; }
      .kpi { flex:1; border:1px solid #ddd; border-radius:4px; padding:6px 8px; text-align:center; }
      .kpi .val { font-size:13px; font-weight:700; }
      .kpi .lbl { font-size:8px; color:#888; margin-top:2px; }
      h3 { color:#2D5C3F; font-size:11px; margin:16px 0 6px; border-bottom:1px solid #D4AF37; padding-bottom:3px; }
      table { width:100%; border-collapse:collapse; margin-bottom:6px; }
      th { background:#2D5C3F; color:#fff; padding:4px 6px; text-align:left; font-size:8px; white-space:nowrap; }
      th.num { text-align:right; }
      td { padding:3px 6px; border-bottom:1px solid #eee; font-size:8px; vertical-align:top; }
      td.num { text-align:right; }
      tr.even td { background:#fff; }
      tr.odd  td { background:#f9fafb; }
      .tech-section { margin-top:14px; page-break-inside:avoid; }
      .tech-header { background:#f0f4f1; color:#2D5C3F; font-weight:700; font-size:10px; padding:5px 8px; border-radius:4px; margin-bottom:4px; display:flex; justify-content:space-between; }
      .tech-meta { font-weight:400; color:#555; font-size:9px; }
      .footer { margin-top:10px; font-size:8px; color:#aaa; display:flex; justify-content:space-between; border-top:1px solid #eee; padding-top:6px; }
    </style>
  </head><body>
    <div class="header">
      <h2>📊 Technician Production Report</h2>
      <div class="meta">${dateRange || ''}<br/>Generated: ${new Date().toLocaleString('en-US')}</div>
    </div>
    <div class="kpis">
      <div class="kpi"><div class="val" style="color:#1565c0">${totals?.total_assigned||0}</div><div class="lbl">Total Assigned</div></div>
      <div class="kpi"><div class="val" style="color:#2e7d32">${totals?.completed||0}</div><div class="lbl">Completed</div></div>
      <div class="kpi"><div class="val" style="color:#c62828">${totals?.cancelled||0}</div><div class="lbl">Cancelled</div></div>
      <div class="kpi"><div class="val" style="color:#2D5C3F">${fmtM(totals?.revenue)}</div><div class="lbl">Total Revenue</div></div>
    </div>
    <h3>Production Summary</h3>
    <table>
      <thead><tr>
        <th>Technician</th>
        <th class="num">Assigned</th><th class="num">Completed</th>
        <th class="num">Cancelled</th><th class="num">In Progress</th>
        <th class="num">Completion %</th><th class="num">Avg Actual</th>
        <th class="num">Avg Sched</th><th class="num">Revenue</th>
        <th class="num">Facilities</th><th>Period</th>
      </tr></thead>
      <tbody>${summaryRows}</tbody>
    </table>
    <h3>Service Detail</h3>
    ${detailSections}
    <div class="footer">
      <span>SafeCore Pest Management</span>
      <span>Confidential — Internal Use Only</span>
    </div>
    <script>window.onload=function(){window.print();}<\/script>
  </body></html>`);
  win.document.close();
}

function printPesticideAnnual(data, filter) {
  if (!data || !data.length) return;
  const stateLabel = filter.state || 'State';
  const fmtDT = (d) => d ? new Date(d).toLocaleString('en-US',{month:'short',day:'2-digit',hour:'2-digit',minute:'2-digit'}) : '—';
  const fmtD  = (d) => d ? new Date(d+'T12:00:00').toLocaleDateString('en-US',{month:'short',day:'2-digit',year:'numeric'}) : '—';
  const METHOD_LABELS = {
    spot_treatment:'Spot Treatment', gel_placement:'Gel Placement',
    bait_station:'Bait Station', crack_crevice:'Crack & Crevice',
    broadcast:'Broadcast', trap_placement:'Trap Placement',
    void_treatment:'Void Treatment', perimeter:'Perimeter',
  };

  const STATE_NOTES = {
    NY: 'NY DEC Commercial Pesticide Applicator — Annual Report of Pesticide Applications',
    NJ: 'NJ DEP Pesticide Control Program — Commercial Pesticide Application Records',
    FL: 'FDACS Division of Agricultural Environmental Services — Pesticide Application Records',
    TX: 'Texas Department of Agriculture — Pesticide Application Records',
    CA: 'CDPR Pesticide Use Report (PUR) — Commercial Applicator Records',
  };

  const applicatorSections = data.map(applicator => {
    const rows = applicator.applications.map((r,i) => `
      <tr class="${i%2===0?'even':'odd'}">
        <td>${fmtDT(r.applied_at)}</td>
        <td style="white-space:normal;max-width:120px">${r.facility_name}<br/><span style="color:#888;font-size:7px">${r.facility_address||''}, ${r.city||''} ${r.state||''}</span></td>
        <td style="white-space:normal;max-width:80px">${r.client_name}</td>
        <td style="font-weight:600;white-space:normal;max-width:100px">${r.product_name}</td>
        <td>${r.epa_number||'—'}</td>
        <td style="white-space:normal;max-width:120px">${r.active_ingredient||'—'}${r.ai_percentage?` <span style="color:#888">(${r.ai_percentage}%)</span>`:''}</td>
        <td>${METHOD_LABELS[r.application_method]||r.application_method||'—'}</td>
        <td>${r.quantity} ${r.unit}</td>
        <td>${r.dilution_rate||(r.dilution_oz_per_gallon?r.dilution_oz_per_gallon+' oz/gal':'—')}</td>
        <td>${r.area_name||'—'}</td>
        <td>${r.order_number}</td>
      </tr>`).join('');

    return `
      <div class="applicator-section">
        <div class="applicator-header">
          <div class="applicator-left">
            <div class="applicator-name">${applicator.applicator_name}</div>
            <div class="applicator-meta">
              ${stateLabel} License: <strong>${applicator.state_license||applicator.ny_license||'N/A'}</strong>
              ${applicator.state_license_type||applicator.ny_license_type ? ' — '+(applicator.state_license_type||applicator.ny_license_type) : ''}
              ${applicator.license_expiry ? ' | Expires: '+fmtD(applicator.license_expiry) : ''}
            </div>
            <div class="applicator-meta">${applicator.company_name} | ${applicator.company_address}, ${applicator.company_city}, ${applicator.company_state} ${applicator.company_zip}</div>
          </div>
          <div class="applicator-badge">${applicator.applications.length} Applications</div>
        </div>
        <table>
          <thead><tr>
            <th>Date/Time</th><th>Site/Facility</th><th>Client</th>
            <th>Product</th><th>EPA Reg #</th><th>Active Ingredient</th>
            <th>Method</th><th>Qty</th><th>Dilution</th><th>Area</th><th>WO #</th>
          </tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </div>`;
  }).join('');

  const win = window.open('', '_blank');
  win.document.write(`<!DOCTYPE html><html><head>
    <title>Pesticide Annual Report — ${stateLabel} ${filter.year||''}</title>
    <style>
      @page { size: landscape; margin: 12mm 10mm; }
      * { box-sizing:border-box; }
      body { font-family:Arial,sans-serif; font-size:9px; color:#222; margin:0; }
      .header { display:flex; justify-content:space-between; align-items:flex-end; margin-bottom:6px; border-bottom:2px solid #2D5C3F; padding-bottom:6px; }
      .header h2 { color:#2D5C3F; margin:0; font-size:13px; }
      .header .sub { color:#555; font-size:8px; margin-top:2px; }
      .header .meta { color:#666; font-size:8px; text-align:right; }
      .applicator-section { margin-bottom:16px; page-break-inside:avoid; }
      .applicator-header { display:flex; justify-content:space-between; align-items:flex-start; background:#f0f4f1; border:1px solid #2D5C3F; border-radius:4px 4px 0 0; padding:8px 12px; }
      .applicator-name { font-size:11px; font-weight:700; color:#2D5C3F; margin-bottom:3px; }
      .applicator-meta { font-size:8px; color:#555; margin-bottom:2px; }
      .applicator-badge { background:#2D5C3F; color:#fff; padding:3px 10px; border-radius:10px; font-size:9px; font-weight:600; white-space:nowrap; }
      table { width:100%; border-collapse:collapse; }
      th { background:#2D5C3F; color:#fff; padding:4px 5px; text-align:left; font-size:7.5px; white-space:nowrap; }
      td { padding:3px 5px; border-bottom:1px solid #eee; font-size:7.5px; vertical-align:top; word-break:break-word; }
      tr.even td { background:#fff; }
      tr.odd  td { background:#f9fafb; }
      .footer { margin-top:8px; font-size:8px; color:#aaa; display:flex; justify-content:space-between; border-top:1px solid #eee; padding-top:4px; }
    </style>
  </head><body>
    <div class="header">
      <div>
        <h2>📄 Pesticide Annual Report — ${stateLabel} ${filter.year||''}</h2>
        <div class="sub">${STATE_NOTES[filter.state]||''}</div>
      </div>
      <div class="meta">
        ${data.reduce((a,ap)=>a+ap.applications.length,0)} total applications &nbsp;|&nbsp; ${data.length} applicator${data.length!==1?'s':''}<br/>
        Generated: ${new Date().toLocaleString('en-US')}
      </div>
    </div>
    ${applicatorSections}
    <div class="footer">
      <span>SafeCore Pest Management</span>
      <span>Confidential — Internal Use Only</span>
    </div>
    <script>window.onload=function(){window.print();}<\/script>
  </body></html>`);
  win.document.close();
}

function statusColor(status) {
  const map = {
    completed: { background:'#e8f5e9', color:'#2e7d32' },
    paid:       { background:'#e8f5e9', color:'#2e7d32' },
    pending:    { background:'#fff8e1', color:'#f57c00' },
    sent:       { background:'#e3f2fd', color:'#1565c0' },
    scheduled:  { background:'#e3f2fd', color:'#1565c0' },
    in_progress:{ background:'#fff3e0', color:'#e65100' },
    overdue:    { background:'#ffebee', color:'#c62828' },
    cancelled:  { background:'#f5f5f5', color:'#616161' },
  };
  return map[status] || { background:'#f5f5f5', color:'#333' };
}

const styles = {
  page:        { padding:'24px', maxWidth:'1400px', margin:'0 auto' },
  pageHeader:  { marginBottom:'28px' },
  pageTitle:   { fontSize:'26px', fontWeight:700, color:GREEN, margin:0 },
  pageSubtitle:{ color:'#666', marginTop:'4px', fontSize:'14px' },
  section:     { marginBottom:'32px' },
  sectionHeader:{ fontSize:'16px', fontWeight:700, color:GREEN, borderBottom:'2px solid '+GOLD, paddingBottom:'6px', marginBottom:'14px' },
  cardGrid:    { display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(240px, 1fr))', gap:'14px' },
  card:        { borderRadius:'10px', padding:'18px', border:'1px solid #ddd', cursor:'pointer', transition:'transform .15s, box-shadow .15s', position:'relative' },
  cardAvail:   { background:'#fff', borderColor:GREEN+'44' },
  cardSoon:    { background:'#fafafa', opacity:0.65, cursor:'default' },
  cardIcon:    { fontSize:'28px', marginBottom:'8px' },
  cardLabel:   { fontWeight:700, fontSize:'14px', color:GREEN, marginBottom:'4px' },
  cardDesc:    { fontSize:'12px', color:'#666', lineHeight:'1.4' },
  badge:       { position:'absolute', top:'10px', right:'10px', background:'#eee', color:'#999', fontSize:'10px', padding:'2px 7px', borderRadius:'10px', fontWeight:600 },
  reportHeader:{ display:'flex', alignItems:'center', gap:'16px', marginBottom:'20px' },
  backBtn:     { background:'none', border:'1px solid '+GREEN, color:GREEN, padding:'6px 14px', borderRadius:'6px', cursor:'pointer', fontWeight:600, fontSize:'13px' },
  filterBar:   { display:'flex', flexWrap:'wrap', gap:'10px', background:'#fff', border:'1px solid #ddd', borderRadius:'10px', padding:'14px', marginBottom:'18px' },
  sel:         { padding:'7px 10px', borderRadius:'6px', border:'1px solid #ccc', fontSize:'13px', minWidth:'180px' },
  inp:         { padding:'7px 10px', borderRadius:'6px', border:'1px solid #ccc', fontSize:'13px' },
  runBtn:      { padding:'7px 18px', background:GREEN, color:'#fff', border:'none', borderRadius:'6px', fontWeight:700, cursor:'pointer', fontSize:'13px' },
  clearBtn:    { padding:'7px 14px', background:'#f5f5f5', color:'#555', border:'1px solid #ddd', borderRadius:'6px', cursor:'pointer', fontSize:'13px' },
  kpiRow:      { display:'flex', gap:'14px', marginBottom:'18px', flexWrap:'wrap' },
  kpiCard:     { flex:'1', minWidth:'150px', background:'#fff', border:'1px solid #ddd', borderRadius:'10px', padding:'16px', textAlign:'center' },
  kpiVal:      { fontSize:'22px', fontWeight:700 },
  kpiLabel:    { fontSize:'12px', color:'#888', marginTop:'4px' },
  countBar:    { fontSize:'13px', color:'#666', marginBottom:'10px' },
  loading:     { textAlign:'center', padding:'40px', color:GREEN, fontSize:'16px' },
  empty:       { textAlign:'center', padding:'60px', color:'#999', fontSize:'15px' },
  tableWrap:   { overflowX:'auto', borderRadius:'10px', border:'1px solid #ddd' },
  table:       { width:'100%', borderCollapse:'collapse', fontSize:'13px' },
  th:          { background:GREEN, color:'#fff', padding:'10px 12px', textAlign:'left', whiteSpace:'nowrap', fontWeight:600 },
  td:          { padding:'9px 12px', borderBottom:'1px solid #eee', whiteSpace:'nowrap' },
  trEven:      { background:'#fff' },
  trOdd:       { background:'#f9fafb' },
  statusBadge: { padding:'2px 9px', borderRadius:'10px', fontSize:'11px', fontWeight:600 },
  csvBtn:   { padding:'7px 14px', background:'#1565c0', color:'#fff', border:'none', borderRadius:'6px', fontWeight:700, cursor:'pointer', fontSize:'13px' },
  printBtn: { padding:'7px 14px', background:'#4a4a4a', color:'#fff', border:'none', borderRadius:'6px', fontWeight:700, cursor:'pointer', fontSize:'13px' },
  nySection:          { marginBottom:'28px' },
  nyApplicatorHeader: { display:'flex', justifyContent:'space-between', alignItems:'flex-start', background:'#f0f4f1', border:'1px solid #2D5C3F', borderRadius:'8px 8px 0 0', padding:'14px 18px', marginBottom:'0' },
  nyApplicatorLeft:   { flex:1 },
  nyApplicatorRight:  { marginLeft:'16px' },
  nyApplicatorName:   { fontSize:'16px', fontWeight:700, color:'#2D5C3F', marginBottom:'4px' },
  nyApplicatorMeta:   { fontSize:'12px', color:'#555', marginBottom:'2px' },
  nyBadge:            { background:'#2D5C3F', color:'#fff', padding:'4px 12px', borderRadius:'12px', fontSize:'12px', fontWeight:600 },
};
